import { ButtonV2 } from '@synoptic/ui-kit/button-v2/button-v2.js';
import { Button } from '@synoptic/ui-kit/button/button.js';
import { CandlestickChartIcon } from '@synoptic/ui-kit/icons/react/candlestick-chart.js';
import { Input } from '@synoptic/ui-kit/input/input.js';
import {
  ModalRoot,
  ModalTrigger,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalClose,
} from '@synoptic/ui-kit/modal/modal.js';
import { Spinner } from '@synoptic/ui-kit/spinner/spinner.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@synoptic/ui-kit/tooltip/tooltip.js';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { paths } from '@/routes-utils/paths';
import { useAuthInfo } from '@/routes/_app/use-auth-info';
import { useDatabentoSymbols } from './use-databento-symbols';
import {
  modalBody,
  searchInput,
  symbolsList,
  groupLabel,
  symbolItem,
  spinnerWrapper,
  truncatedHint,
  viewPriceChartButton,
} from './view-price-chart-link.css';
import { adminAction } from '@/routes/_app/sidebar/menu/menu.css';

type ViewPriceChartLinkProps = (
  | { postId: string; streamId?: never }
  | { streamId: string; postId?: never }
) & { icon?: boolean };

const DATASET_EXCHANGE: Record<string, string> = {
  'EQUS.MINI': 'Equities',
  'GLBX.MDP3': 'Futures',
};

function buildChartHref(
  props: ViewPriceChartLinkProps,
  ticker: string,
): string {
  const idParam = props.postId
    ? `postId=${encodeURIComponent(props.postId)}`
    : `streamId=${encodeURIComponent(props.streamId!)}`;
  return `${paths.toolsKiyotakaChart}?${idParam}&symbol=${encodeURIComponent(ticker)}`;
}

type TickerPickerContentProps = ViewPriceChartLinkProps;

const TickerPickerContent = (props: TickerPickerContentProps) => {
  const [query, setQuery] = useState('');
  const { symbols, loading } = useDatabentoSymbols();

  const { filtered, totalMatches } = useMemo(() => {
    const q = query.toUpperCase();
    const matches = q
      ? symbols.filter((s) => s.ticker.toUpperCase().includes(q))
      : symbols;
    return { filtered: matches.slice(0, 100), totalMatches: matches.length };
  }, [symbols, query]);

  const grouped = useMemo(() => {
    const groups = new Map<string, string[]>();
    for (const s of filtered) {
      const label = DATASET_EXCHANGE[s.dataset] ?? s.dataset;
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label)!.push(s.ticker);
    }
    return groups;
  }, [filtered]);

  if (loading) {
    return (
      <div className={spinnerWrapper}>
        <Spinner size={24} />
      </div>
    );
  }

  return (
    <div className={modalBody}>
      <Input
        className={searchInput}
        placeholder="Search ticker..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
      />
      <div className={symbolsList}>
        {grouped.size === 0 && <span className={symbolItem}>No results</span>}
        {Array.from(grouped.entries()).map(([label, tickers]) => (
          <div key={label}>
            {grouped.size > 1 && <div className={groupLabel}>{label}</div>}
            {tickers.map((t) => (
              <ModalClose key={t} asChild>
                <a
                  className={symbolItem}
                  href={buildChartHref(props, t)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t}
                </a>
              </ModalClose>
            ))}
          </div>
        ))}
        {totalMatches > filtered.length && (
          <div className={truncatedHint}>
            Showing first 100 of {totalMatches} results
          </div>
        )}
      </div>
    </div>
  );
};

export const TickerPickerModalContent = (props: ViewPriceChartLinkProps) => (
  <ModalContent>
    <ModalHeader>
      <ModalTitle>Select ticker</ModalTitle>
    </ModalHeader>
    <TickerPickerContent {...props} />
  </ModalContent>
);

export const ViewPriceChart = ({ icon, ...props }: ViewPriceChartLinkProps) => {
  const { isAdmin } = useAuthInfo();
  const [open, setOpen] = useState(false);

  if (!isAdmin) return null;

  const picker = (
    <ModalContent>
      <ModalHeader>
        <ModalTitle>Select ticker</ModalTitle>
      </ModalHeader>
      <TickerPickerContent {...props} />
    </ModalContent>
  );

  if (icon) {
    return (
      <span onClick={(e) => e.stopPropagation()}>
        <Tooltip>
          <TooltipTrigger asChild>
            <ButtonV2
              type="button"
              variant="secondary"
              size="icon"
              aria-label="View chart"
              className={adminAction}
              onClick={() => setOpen(true)}
            >
              <CandlestickChartIcon />
            </ButtonV2>
          </TooltipTrigger>
          <TooltipContent side="bottom" description="View chart" />
        </Tooltip>
        <ModalRoot open={open} onOpenChange={setOpen}>
          {picker}
        </ModalRoot>
      </span>
    );
  }

  return (
    <span onClick={(e) => e.stopPropagation()}>
      <ModalRoot>
        <ModalTrigger asChild>
          <Button
            startIcon={<CandlestickChartIcon />}
            size="xSmall"
            variant="ghost"
            className={clsx(adminAction, viewPriceChartButton)}
          >
            View chart
          </Button>
        </ModalTrigger>
        {picker}
      </ModalRoot>
    </span>
  );
};
